import { TimelineLite, TweenLite, Power3, Linear } from 'gsap'
import { isMobile } from '../utilities'
import VideoPlayer from '../video-player'

const TIME_PER_THUMB = 2;
const TIME_PER_THUMB_DX = 3;
let focusedItemIndex = -1;
let isListeningToScroll = false;
const items = [];
const PAN_THRESHOLD = 10;

export const didScroll = e => {
  const height = window.innerHeight;
  let fIndex = -1;
  let rect;
  let distanceToCenter = undefined;
  if( !VideoPlayer.isVisible ){
    for (let i = 0; i < items.length; i++) {
      let item = items[i];
      rect = item.thumb.getBoundingClientRect();
      if( fIndex === -1 ){
        // check if first item is entirely visible
        if (rect.top > 0 && rect.bottom < height) {
          fIndex = i;
          distanceToCenter = Math.abs((height * 0.5) - (rect.top + rect.height * 0.5));
        }
      } else {
        // check if item is closer to the center
        let newDistance = Math.abs((height * 0.5) - (rect.top + rect.height * 0.5));
        if (newDistance < distanceToCenter) {
          fIndex = i;
          distanceToCenter = newDistance;
        }
      }
    }
  }
  if( fIndex !== focusedItemIndex ) {
    if( focusedItemIndex>-1 ) {
      items[focusedItemIndex].isFocused = false;
      if( !isMobile() && focusedItemIndex>0 ){
        items[focusedItemIndex+1].isFocused = false;
      }
    }
    focusedItemIndex = fIndex;
    if( focusedItemIndex>-1 ) {
      items[focusedItemIndex].isFocused = true;
      if( !isMobile() && focusedItemIndex>0 ){
        items[focusedItemIndex+1].isFocused = true;
      }
    }
  }
};

const SPEED_HISTORY_LENGTH = 5;

export default class {
  constructor(el) {
    this.__isFocused = false;

    this.el = el;
    this.features = el.querySelectorAll('.details-list:first-child ul li');
    this.contributions = el.querySelectorAll('.details-list:last-child ul li');

    this.thumb = this.el.querySelector('.thumbs');
    this.thumb.addEventListener( 'click', this.playVideo.bind(this) );

    let images = this.el.querySelectorAll('.thumb');
    this.thumbsCount = images.length;
    this.__thumbsPosition = 0;
    //console.log('is MObile? ' + isMobile() );
    if( images.length > 1 ) {
      this.timeline = ( isMobile() ? this.createMobileCarousel( images ) : this.createDxCarousel( images ) );
      this.addGestureListener();
    }

    items.push(this);

    if( !isListeningToScroll ) {
      document.addEventListener('scroll', didScroll);
      isListeningToScroll = true;
    }
  }

  playVideo(){
    VideoPlayer.showVideo( this.el.dataset['videoUrl'], this.el.dataset['videoWidth'], this.el.dataset['videoHeight'] );
  }

  set isFocused(v) {
    this.__isFocused = v;
    if (v) {
      if (this.thumbsCount > 1 && !this.autoTween) {
        this.autoplay();
      }
    }
  }

  get isFocused() {
    return this.__isFocused;
  }

  set thumbsPosition(v) {
    this.__thumbsPosition = v;
    while (v < 0) {
      v += this.thumbsCount - 1;
    }
    if (v > this.thumbsCount - 1) {
      v %= this.thumbsCount - 1;
    }
    this.timeline.seek(v);
  }

  get thumbsPosition() {
    return this.__thumbsPosition;
  }

  createMobileCarousel( images ){
    const tl = new TimelineLite();

    var t = -1;
    const ease = Linear.easeNone;

    for (let i = 0; i < images.length; i++) {
      let img = images[i];

      if (i > 0) {
        tl.from(img, 1, {
          opacity: 0,
          scaleX: 0.9,
          scaleY: 0.9,
          transformOrigin: '100% 50%',
          ease
        }, t);
      }
      tl.set(img, {css: {
        filter: 'brightness(1)',
        zIndex: 1
      }});
      tl.to(img, 1, {
        x: -window.innerWidth,
        ease
      }, t + 1);
      tl.to(img, 1, {
        css: {
          filter: 'brightness(0)'
        },
        ease
      }, t + 1);
      t += 1;
    }

    tl.pause();
    return tl;
  }

  createDxCarousel( images ){
    const tl = new TimelineLite();

    const ease = Linear.easeNone;
    const zoom = 0.055;
    const crossFade = 0.2; // percentage
    const finalZoom = 1 + zoom;

    for (let i = 0; i < images.length; i++) {
      let img = images[i];
      const isFirst = i===0;

      tl.set(img, {css: {
        zIndex: 1
      }}, isFirst ? 0 : i-crossFade);

      if( !isFirst ){

        tl.fromTo( img, crossFade, {
          opacity: 0
        }, {
          opacity: 1,
          ease
        }, i - crossFade);
      }

      let initialScale = (isFirst ? 1 + zoom*crossFade : 1);
      let duration = (isFirst ? 1 : 1+crossFade);
      let offset = (isFirst ? 0 : crossFade);

      tl.fromTo( img, duration, {
        scaleX: initialScale, scaleY: initialScale
      }, {
        scaleX:finalZoom, scaleY:finalZoom,
        transformOrigin: '50% 50%',
        ease
      }, i-offset);
    }

    tl.pause();
    return tl;
  }

  addGestureListener() {
    this.thumb.addEventListener('touchstart', this.didTouch.bind(this));
    this.bindedReleaseTouch = this.didReleaseTouch.bind(this);
    this.bindedCancelTouch = this.didCancelTouch.bind(this);
    this.bindedMoveTouch = this.didMoveTouch.bind(this);
  }

  addTransientTouchListeners() {
    this.thumb.addEventListener('touchend', this.bindedReleaseTouch);
    this.thumb.addEventListener('touchcancel', this.bindedCancelTouch);
    this.thumb.addEventListener('touchmove', this.bindedMoveTouch);
  }

  removeTransientTouchListeners() {
    this.thumb.removeEventListener('touchend', this.bindedReleaseTouch);
    this.thumb.removeEventListener('touchcancel', this.bindedCancelTouch);
    this.thumb.removeEventListener('touchmove', this.bindedMoveTouch);
  }

  didTouch(e) {
    if (this.autoTween) {
      this.autoTween.pause();
    }
    //console.log(e);
    this.initialTouchX = e.touches[0].pageX;
    this.initialTouchY = e.touches[0].pageY;
    this.lastTouchX = this.initialTouchX;
    this.lastTouchMove = performance.now();
    this.initialThumbsPosition = this.thumbsPosition;
    this.isSwiping = false;
    this.canSwipe = true;
    this.speedsHistory = [];
    this.addTransientTouchListeners();
  }

  didMoveTouch(e) {
    const touchX = e.touches[0].pageX;
    const t = performance.now();
    if( !this.isSwiping && this.canSwipe ) {
      const delta = Math.abs(this.initialTouchX - touchX);
      if (delta > PAN_THRESHOLD) {
        const touchY = e.touches[0].pageY;
        const deltaY = Math.abs(this.initialTouchY - touchY);
        if( deltaY > delta ){
          this.canSwipe = false;
        }else{
          this.isSwiping = true;
          if (this.autoTween) {
            this.autoTween.kill();
            this.autoTween = null;
          }
        }
      }
    }
    if (this.isSwiping) {
      let offset = this.initialTouchX - touchX;
      this.thumbsPosition = this.initialThumbsPosition + (offset / window.innerWidth); // cache width
      this.updateAverageSpeed((touchX - this.lastTouchX) / (t - this.lastTouchMove));
    }
    this.lastTouchX = touchX;
    this.lastTouchMove = t;
  }

  updateAverageSpeed(speed) {
    this.speedsHistory.push(speed);

    while (this.speedsHistory.length > SPEED_HISTORY_LENGTH) {
      this.speedsHistory.shift();
    }
  }

  getAverageSpeed() {
    let res = 0;
    for (var i = 0; i < this.speedsHistory.length; i++) {
      res += this.speedsHistory[i];
    }
    if (res === 0) {
      return 0;
    }
    return res / this.speedsHistory.length;
  }

  didCancelTouch() {
  //  console.log('TOUCH CANCEL');
  }

  didReleaseTouch() {
    this.removeTransientTouchListeners();
    const speed = this.getAverageSpeed();
    if (speed > 0.5) {
      // go fast backwards
      this.autoTween = TweenLite.to(this, 0.5, {
        thumbsPosition: Math.floor(this.thumbsPosition),
        ease: Power3.easeOut,
        onComplete: this.autoplay.bind(this)
      });
    } else if (speed < -0.5) {
      // go fast forward
      this.autoTween = TweenLite.to(this, 0.5, {
        thumbsPosition: Math.ceil(this.thumbsPosition),
        ease: Power3.easeOut,
        onComplete: this.autoplay.bind(this)
      });
    } else {
      // resume
      this.autoTween = TweenLite.to(this, TIME_PER_THUMB, {
        thumbsPosition: Math.ceil(this.thumbsPosition),
        ease: Power3.easeInOut,
        onComplete: this.autoplay.bind(this)
      });
    }
    //console.log( 'average speed: ' +  speed);
  }

  autoplay() {
    if (!this.isFocused) {
      //console.log('NO AUTOPLAY BECAUSE BLURRED');
      this.autoTween = null;
      return;
    }
    this.autoTween = TweenLite.to(this,
      isMobile() ? TIME_PER_THUMB: TIME_PER_THUMB_DX,
      {
        thumbsPosition: this.thumbsPosition + 1,
        ease: isMobile() ? Power3.easeInOut : Linear.easeNone,
        onComplete: this.autoplay.bind(this)
      });
  }
}


/** WEBPACK FOOTER **
 ** ./src/js/portfolio/portfolio-item.js
 **/